import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Button, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import WorkIcon from '@mui/icons-material/Work';
import api from '../utils/axiosConfig';

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4),
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #121212 0%, #2C2C2C 100%)',
  [theme.breakpoints.up('md')]: { maxWidth: '1200px' }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #2D2D2D 0%, #1A1A1A 100%)',
  border: '1px solid rgba(255, 215, 0, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
}));

const statusColor = (s) => {
  if (s === 'ACTIVE') return 'success';
  if (s === 'CLOSED') return 'error';
  return 'warning';
};

const EmployerJobList = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/employer/jobs')
      .then(res => setJobs(res.data))
      .catch(() => setError('Failed to load jobs'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress sx={{ color: '#FFD700' }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #121212 0%, #2C2C2C 100%)', py: 4 }}>
      <StyledContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WorkIcon sx={{ color: '#FFD700', fontSize: '2rem' }} />
            <Typography variant="h4" sx={{ color: '#FFF', fontWeight: 'bold' }}>My Jobs</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/employer/jobs/new')}
            sx={{
              background: 'linear-gradient(90deg, #FFD700, #009639)',
              color: '#000', fontWeight: 600, textTransform: 'none',
              '&:hover': { background: 'linear-gradient(90deg, #009639, #FFD700)' }
            }}
          >
            Post New Job
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {jobs.length === 0 ? (
          <StyledPaper sx={{ textAlign: 'center', py: 6 }}>
            <WorkIcon sx={{ fontSize: 60, color: 'rgba(255,215,0,0.3)', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#FFF', mb: 1 }}>No jobs posted yet</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>Post your first job to start attracting talent!</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/employer/jobs/new')}
              sx={{ background: 'linear-gradient(90deg, #FFD700, #009639)', color: '#000', fontWeight: 600, textTransform: 'none' }}>
              Post Your First Job
            </Button>
          </StyledPaper>
        ) : (
          <StyledPaper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Job Title', 'Location', 'Type', 'Status', 'Applications', 'Posted'].map(h => (
                      <TableCell key={h} sx={{ color: '#FFD700', fontWeight: 600, borderBottom: '1px solid rgba(255,215,0,0.2)' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map(job => (
                    <TableRow key={job.id} hover onClick={() => navigate(`/employer/jobs/${job.id}`)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,215,0,0.05)' } }}>
                      <TableCell sx={{ color: '#FFF', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{job.title}</TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{job.location || '—'}</TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{(job.type || '').replace('_', ' ')}</TableCell>
                      <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <Chip label={job.status || 'ACTIVE'} color={statusColor(job.status)} size="small" />
                      </TableCell>
                      <TableCell sx={{ color: '#FFD700', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{job._count?.applications ?? 0}</TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{new Date(job.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledPaper>
        )}
      </StyledContainer>
    </Box>
  );
};

export default EmployerJobList;
